import React, { ChangeEvent, createContext, ReactNode, useCallback, useContext, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';

import { SearchIcon } from './icons';
import {
  CoworkerAvatar,
  CoworkerBuilderCancel,
  CoworkerBuilderFooter,
  CoworkerBuilderHint,
  CoworkerBuilderMeta,
  CoworkerBuilderNameInput,
  CoworkerCheckbox,
  CoworkerEmptyState,
  CoworkerFormError,
  CoworkerPickList,
  CoworkerPickRow,
  CoworkerRowName,
  CoworkerRowSubtitle,
  CoworkerRowText,
  CoworkerSearchField,
  CoworkerSearchInput,
  CoworkerShowMoreButton,
  FiltersSheetCtaButton,
} from './styles';
import { useCoworkerGroupMutations } from 'data/mutations/useCoworkerGroupMutations.hook';
import { useCoworkerSearchQuery } from 'data/queries/useCoworkerSearchQuery.hook';
import { useDebouncedValue } from 'hooks/use-debounced-value.hook';
import { COWORKER_GROUP_MEMBER_CAP, CoworkerGroupResponse, CoworkerResponse } from 'store/api-client/coworker-types';

export type CoworkerGroupBuilderState = { mode: 'create' } | { mode: 'edit'; group: CoworkerGroupResponse };

export interface CoworkerGroupBuilderProps {
  state: CoworkerGroupBuilderState;
  /** The sheet's day — the picker's rows are the same directory the sheet lists. */
  date: string;
  onCancel: VoidFunction;
  onSaved: (group: CoworkerGroupResponse) => void;
}

interface PickRowProps {
  coworker: CoworkerResponse;
  isChecked: boolean;
  isDisabled: boolean;
  onToggle: (userUuid: string) => void;
}

interface BuilderContextValue {
  name: string;
  onNameChange: (event: ChangeEvent<HTMLInputElement>) => void;
  search: string;
  onSearchChange: (event: ChangeEvent<HTMLInputElement>) => void;
  coworkers: CoworkerResponse[];
  total: number;
  hasMore: boolean;
  loadMore: VoidFunction;
  isLoadingMore: boolean;
  isLoading: boolean;
  isError: boolean;
  selected: ReadonlySet<string>;
  toggle: (userUuid: string) => void;
  isAtCap: boolean;
  canSave: boolean;
  hasSaveFailed: boolean;
  save: VoidFunction;
  cancel: VoidFunction;
}

const SEARCH_DEBOUNCE_MS = 250;

const BuilderContext = createContext<BuilderContextValue | null>(null);

const useBuilder = (): BuilderContextValue => {
  const value = useContext(BuilderContext);

  if (!value) {
    throw new Error('CoworkerGroupBuilder parts must be rendered inside CoworkerGroupBuilderProvider');
  }

  return value;
};

/**
 * The group builder's state (§C3): a checkbox picker over the visible directory plus an optional name,
 * used both to create a group and to edit one (rename, add/remove members — CV-TEAM-06). Save needs at
 * least one member and stays within the BE's cap; the name defaults to "New group" when left blank.
 *
 * The picker searches the directory the same way the sheet does, so a large company is pickable
 * without scrolling a hundred rows; members already in the group stay selected even when the search
 * hides them, and are sent back untouched.
 *
 * Split into a provider and two parts so the sheet can scroll the fields while the Save / Cancel
 * footer stays pinned — the provider holds the state, `Body` and `Footer` render it.
 */
export const CoworkerGroupBuilderProvider = ({
  state,
  date,
  onCancel,
  onSaved,
  children,
}: CoworkerGroupBuilderProps & { children: ReactNode }): JSX.Element => {
  const intl = useIntl();
  const editing = state.mode === 'edit' ? state.group : null;
  const [name, setName] = useState(editing?.name ?? '');
  const [selected, setSelected] = useState<ReadonlySet<string>>(() => new Set(editing?.memberUserUuids ?? []));
  const [search, setSearch] = useState('');
  const [hasSaveFailed, setHasSaveFailed] = useState(false);
  const query = useDebouncedValue(search.trim(), SEARCH_DEBOUNCE_MS);
  const { coworkers, total, hasMore, loadMore, isLoadingMore, isLoading, isError } = useCoworkerSearchQuery({
    query,
    date,
  });
  const { createGroup, updateGroup, isSaving } = useCoworkerGroupMutations();

  const onNameChange = useCallback((event: ChangeEvent<HTMLInputElement>) => setName(event.target.value), []);
  const onSearchChange = useCallback((event: ChangeEvent<HTMLInputElement>) => setSearch(event.target.value), []);

  const toggle = useCallback((userUuid: string) => {
    setSelected(current => {
      const next = new Set(current);

      if (next.has(userUuid)) {
        next.delete(userUuid);
      } else if (next.size < COWORKER_GROUP_MEMBER_CAP) {
        next.add(userUuid);
      }

      return next;
    });
  }, []);

  const memberUserUuids = useMemo(() => [...selected], [selected]);
  const isAtCap = selected.size >= COWORKER_GROUP_MEMBER_CAP;
  const canSave = selected.size > 0 && !isSaving;

  const save = useCallback(async () => {
    setHasSaveFailed(false);
    const groupName = name.trim() || intl.formatMessage({ id: 'newGroup' });

    try {
      const saved = editing
        ? await updateGroup({ groupUuid: editing.uuid, body: { name: groupName, memberUserUuids } })
        : await createGroup({ name: groupName, memberUserUuids });

      onSaved(saved);
    } catch {
      setHasSaveFailed(true);
    }
  }, [name, intl, editing, updateGroup, createGroup, memberUserUuids, onSaved]);

  const value = useMemo<BuilderContextValue>(
    () => ({
      name,
      onNameChange,
      search,
      onSearchChange,
      coworkers,
      total,
      hasMore,
      loadMore,
      isLoadingMore,
      isLoading,
      isError,
      selected,
      toggle,
      isAtCap,
      canSave,
      hasSaveFailed,
      save,
      cancel: onCancel,
    }),
    [
      name,
      onNameChange,
      search,
      onSearchChange,
      coworkers,
      total,
      hasMore,
      loadMore,
      isLoadingMore,
      isLoading,
      isError,
      selected,
      toggle,
      isAtCap,
      canSave,
      hasSaveFailed,
      save,
      onCancel,
    ],
  );

  return <BuilderContext.Provider value={value}>{children}</BuilderContext.Provider>;
};

const PickRow = ({ coworker, isChecked, isDisabled, onToggle }: PickRowProps): JSX.Element => {
  const handleToggle = useCallback(() => onToggle(coworker.userUuid), [onToggle, coworker.userUuid]);

  return (
    <li>
      <CoworkerPickRow
        type="button"
        role="checkbox"
        aria-checked={isChecked}
        data-testid={`coworker-pick-${coworker.userUuid}`}
        disabled={isDisabled}
        onClick={handleToggle}
      >
        <CoworkerCheckbox isChecked={isChecked} aria-hidden>
          {isChecked ? '✓' : ''}
        </CoworkerCheckbox>
        <CoworkerAvatar aria-hidden>{coworker.initials}</CoworkerAvatar>
        <CoworkerRowText>
          <CoworkerRowName>{coworker.name}</CoworkerRowName>
          {coworker.role && <CoworkerRowSubtitle>{coworker.role}</CoworkerRowSubtitle>}
        </CoworkerRowText>
      </CoworkerPickRow>
    </li>
  );
};

/** The scrolling part: hint, name, search and the picker. */
export const CoworkerGroupBuilderBody = (): JSX.Element => {
  const intl = useIntl();
  const {
    name,
    onNameChange,
    search,
    onSearchChange,
    coworkers,
    total,
    hasMore,
    loadMore,
    isLoadingMore,
    isLoading,
    isError,
    selected,
    toggle,
    isAtCap,
  } = useBuilder();

  return (
    <div data-testid="coworker-group-builder">
      <CoworkerBuilderHint>{intl.formatMessage({ id: 'coworkerGroupHint' })}</CoworkerBuilderHint>
      <CoworkerBuilderNameInput
        type="text"
        data-testid="coworker-group-name"
        placeholder={intl.formatMessage({ id: 'groupName' })}
        aria-label={intl.formatMessage({ id: 'groupName' })}
        maxLength={255}
        autoFocus
        value={name}
        onChange={onNameChange}
      />
      <CoworkerSearchField>
        <SearchIcon />
        <CoworkerSearchInput
          type="text"
          data-testid="coworker-group-search"
          placeholder={intl.formatMessage({ id: 'searchByName' })}
          aria-label={intl.formatMessage({ id: 'searchByName' })}
          value={search}
          onChange={onSearchChange}
        />
      </CoworkerSearchField>
      {isError && (
        <CoworkerEmptyState>
          <strong>{intl.formatMessage({ id: 'coworkerSearchFailed' })}</strong>
        </CoworkerEmptyState>
      )}
      {!isError && !isLoading && coworkers.length === 0 && (
        <CoworkerEmptyState data-testid="coworker-group-picker-empty">
          <strong>{intl.formatMessage({ id: 'noCoworkersFound' })}</strong>
          <span>{intl.formatMessage({ id: 'tryAnotherName' })}</span>
        </CoworkerEmptyState>
      )}
      {!isError && coworkers.length > 0 && (
        <CoworkerPickList data-testid="coworker-group-picker" aria-busy={isLoading}>
          {coworkers.map(coworker => {
            const isChecked = selected.has(coworker.userUuid);

            return (
              <PickRow
                key={coworker.userUuid}
                coworker={coworker}
                isChecked={isChecked}
                isDisabled={!isChecked && isAtCap}
                onToggle={toggle}
              />
            );
          })}
        </CoworkerPickList>
      )}
      {!isError && hasMore && (
        <CoworkerShowMoreButton
          type="button"
          data-testid="coworker-group-picker-more"
          disabled={isLoadingMore}
          onClick={loadMore}
        >
          {intl.formatMessage({ id: 'showMoreCoworkers' }, { shown: coworkers.length, total })}
        </CoworkerShowMoreButton>
      )}
    </div>
  );
};

/** The pinned part: the selection count, a save failure, Save and Cancel. */
export const CoworkerGroupBuilderFooter = (): JSX.Element => {
  const intl = useIntl();
  const { selected, canSave, hasSaveFailed, save, cancel } = useBuilder();

  return (
    <CoworkerBuilderFooter>
      <CoworkerBuilderMeta data-testid="coworker-group-selected">
        {intl.formatMessage({ id: 'membersSelected' }, { count: selected.size, cap: COWORKER_GROUP_MEMBER_CAP })}
      </CoworkerBuilderMeta>
      {hasSaveFailed && (
        <CoworkerFormError role="alert" data-testid="coworker-group-save-error">
          {intl.formatMessage({ id: 'coworkerGroupSaveFailed' })}
        </CoworkerFormError>
      )}
      <FiltersSheetCtaButton type="button" data-testid="coworker-group-save" disabled={!canSave} onClick={save}>
        {intl.formatMessage({ id: 'saveGroup' })}
      </FiltersSheetCtaButton>
      <CoworkerBuilderCancel type="button" data-testid="coworker-group-cancel" onClick={cancel}>
        {intl.formatMessage({ id: 'cancel' })}
      </CoworkerBuilderCancel>
    </CoworkerBuilderFooter>
  );
};

/** Body and footer together — for a surface that does not need the footer pinned separately. */
export const CoworkerGroupBuilder = (props: CoworkerGroupBuilderProps): JSX.Element => (
  <CoworkerGroupBuilderProvider {...props}>
    <CoworkerGroupBuilderBody />
    <CoworkerGroupBuilderFooter />
  </CoworkerGroupBuilderProvider>
);
